/**
 * Logout Service
 *
 * Comprehensive logout handling that cleans up:
 * - Server-side session
 * - Firebase auth state
 * - All Zustand stores
 * - All localStorage data
 */

import {
  signOut,
  endSession,
  getStoredSessionId,
  clearStoredSessionId,
} from '@perfin/shared';
import { useAuthStore } from '@/stores/authStore';
import { useItemStore } from '@/stores/itemStore';
import { useUIStore } from '@/stores/uiStore';
import { useSettingsStore } from '@/stores/settingsStore';
import { useNotificationStore } from '@/stores/notificationStore';

// ============================================================================
// LOCALSTORAGE CLEANUP
// ============================================================================

/** Keys that should be cleared on logout */
const PERFIN_STORAGE_KEYS = [
  'perfin-ui-store',
  'perfin_session_id',
  'perfin_settings_tab',
];

/** Prefix patterns for dynamic keys (item drafts, etc.) */
const PERFIN_KEY_PREFIXES = [
  'perfin_item_draft_',
  'perfin_subitem_draft_',
];

/**
 * Clear all Perfin-related localStorage entries
 */
function clearLocalStorage(): void {
  // Clear known keys
  for (const key of PERFIN_STORAGE_KEYS) {
    localStorage.removeItem(key);
  }

  // Clear prefixed keys (drafts, etc.)
  const keysToRemove: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key) {
      for (const prefix of PERFIN_KEY_PREFIXES) {
        if (key.startsWith(prefix)) {
          keysToRemove.push(key);
          break;
        }
      }
    }
  }
  for (const key of keysToRemove) {
    localStorage.removeItem(key);
  }
}

// ============================================================================
// STORE CLEANUP
// ============================================================================

/**
 * Reset all Zustand stores to their initial state
 */
function resetStores(): void {
  // Clear item subscriptions first to prevent permission errors
  useItemStore.getState().clearSubscriptions();

  // Reset auth store (but we'll call signOut separately)
  useAuthStore.setState({
    user: null,
    profile: null,
    loading: false,
    error: null,
    mfaPending: false,
    mfaSetupRequired: false,
    pendingUser: null,
  });

  // Reset item store
  useItemStore.setState({
    items: [],
    subItemsMap: {},
    subscribedUserId: null,
    loading: false,
    error: null,
  });

  // Reset notification store
  useNotificationStore.setState({
    notifications: [],
    subscribedUserId: null,
    loading: false,
    error: null,
  });

  // Reset settings store (keep default structure but clear data)
  useSettingsStore.setState({
    organization: null,
    members: [],
    invitations: [],
    categories: [],
    entities: [],
    accounts: [],
    currentUserId: null,
    currentUserRole: null,
    initialized: null,
    loading: false,
    error: null,
  });

  // UI store is persisted, so we need to explicitly reset persisted values
  // The transient values (toasts, modals) reset automatically
  useUIStore.setState({
    activeModal: null,
    modalData: null,
    confirmConfig: null,
    toasts: [],
    globalLoading: false,
    searchQuery: '',
  });
}

// ============================================================================
// MAIN LOGOUT FUNCTION
// ============================================================================

export interface LogoutOptions {
  /** Skip server session termination (for expired sessions) */
  skipServerSession?: boolean;
  /** Reason for logout (for logging/analytics) */
  reason?: 'user' | 'timeout' | 'expired' | 'remote';
}

/**
 * Perform comprehensive logout with full cleanup.
 *
 * Call sequence:
 * 1. End server session (if not skipped)
 * 2. Sign out of Firebase Auth
 * 3. Reset all Zustand stores
 * 4. Clear localStorage
 */
export async function performLogout(options: LogoutOptions = {}): Promise<void> {
  const { skipServerSession = false, reason = 'user' } = options;

  console.log(`[LogoutService] Performing logout (reason: ${reason})`);

  try {
    // 1. End server session (if we have one and not skipping)
    if (!skipServerSession) {
      const sessionId = getStoredSessionId();
      if (sessionId) {
        try {
          await endSession(sessionId);
        } catch (error) {
          // Session might already be expired/invalid - that's okay
          console.warn('[LogoutService] Failed to end server session:', error);
        }
      }
    }

    // 2. Sign out of Firebase Auth
    try {
      await signOut();
    } catch (error) {
      // Auth might already be signed out
      console.warn('[LogoutService] Firebase signOut error:', error);
    }

    // 3. Reset all stores
    resetStores();

    // 4. Clear localStorage
    clearLocalStorage();
    clearStoredSessionId();

    console.log('[LogoutService] Logout complete');
  } catch (error) {
    console.error('[LogoutService] Logout error:', error);
    // Even on error, try to clean up client state
    resetStores();
    clearLocalStorage();
    clearStoredSessionId();
  }
}

/**
 * Handle session expired error.
 * Called when a Cloud Function returns SESSION_EXPIRED.
 */
export async function handleSessionExpired(): Promise<void> {
  await performLogout({
    skipServerSession: true, // Session is already gone on server
    reason: 'expired',
  });
}

/**
 * Handle remote logout (another device logged out this session).
 * Called when heartbeat or status check returns inactive.
 */
export async function handleRemoteLogout(): Promise<void> {
  await performLogout({
    skipServerSession: true, // Session was ended by another device
    reason: 'remote',
  });
}
